import React, {useEffect, useState} from 'react';
import {Box} from '@mui/system';
import {Button, Alert} from '@mui/material';
import {useReviewContext} from '../../contexts/ReviewContext';
import {RiskNotesFormWrappedContainer} from './RiskNotesForm';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import {RiskNotesProvider, useRiskNotesMutations} from './riskNotesQueries';
import {Prompt} from '../../components/Prompt/Prompt';
import Loading from '../../components/Loading/Loading';
import PrintableRiskNotesForm from './PrintableRiskNotesForm';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';

import {GlobalPermissionEnum} from '../../schema-types/globalTypes';

export const RiskNotesPage: React.FC<{readOnly?: boolean}> = ({readOnly = false}) => {
  return (
    <>
      <HandoverStep />
      <RiskNotesProvider>
        <RiskNotesPageInner readOnly={readOnly} />
      </RiskNotesProvider>
    </>
  );
};
export const RiskNotesPageInner: React.FC<{readOnly?: boolean}> = ({
  readOnly = false,
}) => {
  const {reviewId} = useReviewContext();
  const [rerenderKey, setRerenderKey] = useState(0);
  const {
    upsertRiskProfile,
    riskData,
    riskLoading,
    saveDummyRiskProfile,
    riskError,
    refetchRiskData,
  } = useRiskNotesMutations();

  // changed to use effect so it runs on load to retrieve the data for upsertRiskProfile
  useEffect(() => {
    if (riskData?.reviewDetails && !riskData?.reviewDetails?.riskProfiles.length) {
      // this part remains the same
      upsertRiskProfile({
        variables: {
          inputs:
            riskData?.reviewDetails.contacts?.map((contact) => ({
              centralPropositionAgreed: false,
              dfmAgreed: false,
              contactId: contact.id,
              reviewId,
            })) || [],
        },
      }).then(() => {
        refetchRiskData() // Refetch to get the updated risk profiles after creation
          .catch((error) => {
            console.error('Error refetching risk data after upsert:', error);
          });
      });
    }
  }, [riskData?.reviewDetails?.riskProfiles.length]);

  const handleCreateDummyRisk = async () => {
    await saveDummyRiskProfile(true);
    // Increment counter to force re-render
    setRerenderKey((prev) => prev + 1);
  };

  if (riskLoading) {
    return <Loading entityName="Risk" />;
  }

  if (
    !riskData?.reviewDetails?.contacts?.length &&
    !riskData?.reviewDetails?.riskProfiles.length
  ) {
    return (
      <Alert severity="info">
        As this review predates the introduction of the Risk Profiling feature, this
        section is unavailable.
      </Alert>
    );
  }
  if (riskError) {
    return (
      <Prompt
        variant="error"
        title="Error loading risk data"
        content={riskError?.message}
      />
    );
  }

  return (
    <>
      {riskData && (
        <Box
          position="relative"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
        >
          <FeatureBoundary
            showErrorIfDenied={false}
            features={[GlobalPermissionEnum.can_access_admin_features]}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCreateDummyRisk}
              size="small"
            >
              Create Dummy Risk Profile
            </Button>
          </FeatureBoundary>
          <PrintableRiskNotesForm />
        </Box>
      )}
      <Box width="100%" pt={2} />

      <RiskNotesFormWrappedContainer
        key={rerenderKey}
        print={false}
        riskData={riskData}
      />
      <Box width="100%" pt={6} />
    </>
  );
};
